import React from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  APP_STORE_URL,
  EVO_DEEP_LINK,
  GOOGLE_PLAY_URL,
  detectAppPlatform,
  storeUrlForPlatform,
} from "./appLinks";
import {
  getPublicReferralUrl,
  getReferralCodeFromLocation,
} from "./referralRouting";
import {
  getCachedAffiliateCodeValidity,
  validateAffiliateCode,
} from "./referralValidation";
import svgPaths from "./svg-paths";

async function copyToClipboard(text: string) {
  try {
    if (
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === "function"
    ) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {}

  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.top = "0";
  ta.style.left = "-9999px";
  document.body.appendChild(ta);
  ta.focus();
  ta.select();
  ta.setSelectionRange(0, ta.value.length);
  const ok = document.execCommand("copy");
  document.body.removeChild(ta);
  if (!ok) throw new Error("execCommand copy failed");
  return true;
}

const FALLBACK_DELAY_MS = 1400;
const COPY_CONFIRM_DELAY_MS = 400;
const COPY_STATE_RESET_MS = 2600;

function wait(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function EvoLogo({ className }: { className: string }) {
  return (
    <svg
      className={className}
      fill="none"
      preserveAspectRatio="none"
      viewBox="0 0 76.8 24"
    >
      <g id="evo-full">
        <path d={svgPaths.p22369200} fill="black" />
        <path d={svgPaths.p369aad00} fill="black" />
        <path d={svgPaths.p3fd92480} fill="black" />
        <path d={svgPaths.p1a0fa080} fill="black" />
        <path d={svgPaths.pf751c0} fill="black" />
      </g>
    </svg>
  );
}

export default function Referrer() {
  const [busy, setBusy] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const platform = React.useMemo(() => detectAppPlatform(), []);

  const rawCode = React.useMemo(
    () => getReferralCodeFromLocation(window.location),
    [],
  );
  const displayCode = React.useMemo(() => rawCode.toUpperCase(), [rawCode]);
  const cachedValidity = React.useMemo(
    () => getCachedAffiliateCodeValidity(rawCode),
    [rawCode],
  );
  const [validationState, setValidationState] = React.useState<
    "loading" | "valid"
  >(cachedValidity === true ? "valid" : "loading");
  const qrUrl = React.useMemo(
    () => getPublicReferralUrl(window.location, displayCode),
    [displayCode],
  );
  const isDesktop = platform === "desktop";
  const storeUrl = platform === "android" ? GOOGLE_PLAY_URL : APP_STORE_URL;

  React.useEffect(() => {
    document.title = displayCode
      ? `${displayCode} - Evo referral code`
      : "Evo referral code";
    if (!rawCode && typeof window !== "undefined") {
      window.location.replace("/");
    }
  }, [displayCode, rawCode]);

  React.useEffect(() => {
    if (!rawCode || validationState === "valid") return;

    let active = true;
    validateAffiliateCode(rawCode)
      .then((valid) => {
        if (!active) return;
        if (valid) {
          setValidationState("valid");
          return;
        }
        window.location.replace("/");
      })
      .catch(() => {
        if (active) window.location.replace("/");
      });

    return () => {
      active = false;
    };
  }, [rawCode, validationState]);

  const openAppOrStore = React.useCallback(() => {
    if (platform === "desktop") {
      window.location.href = storeUrl;
      return;
    }

    let shouldFallback = true;
    const fallbackTimer = window.setTimeout(() => {
      if (shouldFallback && document.visibilityState === "visible") {
        window.location.href = storeUrlForPlatform(platform);
      }
    }, FALLBACK_DELAY_MS);

    const cancelFallback = () => {
      shouldFallback = false;
      window.clearTimeout(fallbackTimer);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") cancelFallback();
    };

    window.addEventListener("pagehide", cancelFallback, { once: true });
    document.addEventListener("visibilitychange", handleVisibilityChange, {
      once: true,
    });
    window.location.href = EVO_DEEP_LINK;
  }, [platform, storeUrl]);

  const openStore = React.useCallback(() => {
    window.location.href = storeUrlForPlatform(platform);
  }, [platform]);

  const handleCopyAndDownloadClick = React.useCallback(
    async () => {
      if (busy) return;
      setBusy(true);
      try {
        await copyToClipboard(displayCode);
        setCopied(true);
        window.setTimeout(() => setCopied(false), COPY_STATE_RESET_MS);
        await wait(COPY_CONFIRM_DELAY_MS);
        openStore();
      } catch {
        setCopied(false);
        openStore();
      } finally {
        window.setTimeout(() => setBusy(false), COPY_STATE_RESET_MS);
      }
    },
    [busy, displayCode, openStore],
  );

  if (validationState !== "valid") {
    return (
      <div className="bg-[#f8f7f6] min-h-dvh flex items-center justify-center px-[24px] py-[48px] font-['Figtree',sans-serif] text-[#07001a]">
        <div className="flex flex-col items-center gap-5">
          <EvoLogo className="h-[36px] w-[115.2px]" />
          <p className="m-0 text-[18px] font-black text-[#776f7b]">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  if (isDesktop) {
    return (
      <main className="min-h-dvh bg-[#f8f7f6] px-5 py-8 font-['Figtree',sans-serif] text-[#07001a]">
        <section className="mx-auto flex min-h-[calc(100dvh-64px)] max-w-[640px] flex-col items-center justify-center text-center">
          <a href="/" aria-label="Evo home">
            <EvoLogo className="h-[54px] w-[172.8px]" />
          </a>

          <h1 className="m-0 mt-12 text-[clamp(42px,8vw,64px)] font-black leading-[1.08]">
            Use this code for free grades!
          </h1>
          <p className="m-0 mt-4 max-w-[390px] text-[18px] font-black leading-[1.5] text-[#665f73]">
            Redeem the code during sign up.
          </p>

          {displayCode ? (
            <div className="mt-8 w-full max-w-[520px] rounded-[20px] bg-white p-4 shadow-[0px_3px_3.5px_rgba(0,0,0,0.05)]">
              <div className="rounded-[12px] border border-dashed border-[#ff381a]/35 bg-[#fff6f4] px-5 py-5">
                <p className="m-0 break-all text-[clamp(36px,8vw,52px)] font-black leading-none tracking-[0.04em] text-[#07001a]">
                  {displayCode}
                </p>
              </div>
            </div>
          ) : null}

          <div className="mt-8 rounded-[20px] bg-white p-4 shadow-[0px_3px_3.5px_rgba(0,0,0,0.05)]">
            <QRCodeSVG
              value={qrUrl}
              size={156}
              bgColor="#ffffff"
              fgColor="#07001a"
              level="M"
            />
          </div>

          <p className="m-0 mt-5 text-[18px] font-black leading-[1.35]">
            Scan this on your phone
          </p>
          <p className="m-0 mt-2 max-w-[420px] text-[15px] font-bold leading-[1.5] text-[#665f73]">
            It opens this same referral page so you can copy the code and
            download Evo from your phone.
          </p>

          <a
            className="mt-8 flex w-full max-w-[340px] items-center justify-center rounded-[12px] bg-[#141221] px-5 py-4 text-[16px] font-black text-white no-underline"
            href={storeUrl}
          >
            Download Evo Free
          </a>

          <div className="mt-4 flex w-full max-w-[360px] flex-col gap-3 sm:flex-row">
            <a
              className="flex flex-1 items-center justify-center rounded-[12px] bg-white px-5 py-4 text-[15px] font-black text-[#07001a] no-underline shadow-[0px_3px_3.5px_rgba(0,0,0,0.05)]"
              href={APP_STORE_URL}
            >
              App Store
            </a>
            <a
              className="flex flex-1 items-center justify-center rounded-[12px] bg-white px-5 py-4 text-[15px] font-black text-[#07001a] no-underline shadow-[0px_3px_3.5px_rgba(0,0,0,0.05)]"
              href={GOOGLE_PLAY_URL}
            >
              Google Play
            </a>
          </div>

          <p className="m-0 mt-7 max-w-[520px] text-[14px] font-semibold leading-[1.5] text-[#9b94a3]">
            Downloading still works if you skip copying, but the code is what
            unlocks the free grades.
          </p>
        </section>
      </main>
    );
  }

  return (
    <div className="bg-[#f8f7f6] min-h-dvh flex flex-col font-['Figtree',sans-serif] text-[#07001a]">
      {/* Nav */}
      <div className="w-full max-w-[1440px] mx-auto px-[24px] py-[24px] flex items-center">
        <a className="h-[24px] w-[76.8px] relative shrink-0" href="/" aria-label="Evo home">
          <EvoLogo className="absolute block size-full" />
        </a>
      </div>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center px-[24px] py-[48px]">
        <div className="flex flex-col items-center text-center max-w-[520px] w-full gap-[28px]">
          <div className="flex flex-col items-center gap-[12px]">
            <h1 className="m-0 font-black text-[clamp(38px,7vw,64px)] leading-[1.02] tracking-[-1px]">
              Use this code for free grades!
            </h1>
            <p className="m-0 max-w-[390px] font-bold text-[#665f73] text-[16px] leading-[1.5]">
              {isDesktop
                ? "Redeem the code during sign up."
                : "You can copy first here, then redeem the code during sign up."}
            </p>
          </div>

          {displayCode && (
            <div className="w-full rounded-[28px] bg-white p-4 shadow-[0px_8px_24px_rgba(7,0,26,0.08)]">
              <div className="rounded-[18px] border border-dashed border-[#ff381a]/35 bg-[#fff6f4] px-5 py-5">
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <p className="m-0 break-all text-[clamp(28px,8vw,44px)] font-black leading-none tracking-[0.04em] text-[#07001a]">
                    {displayCode}
                  </p>
                  {copied ? (
                    <span className="rounded-full bg-[#e6f8dc] px-3 py-1 text-[13px] font-black leading-[1.3] text-[#2b8a0f]">
                      Copied to clipboard
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          )}

          {!isDesktop ? (
            <div className="flex w-full flex-col gap-[12px]">
              <button
                className="flex w-full cursor-pointer items-center justify-center rounded-[18px] bg-[#ff381a] px-[24px] py-[17px] text-white shadow-[0px_3px_3.5px_rgba(0,0,0,0.05)] transition-transform duration-150 hover:scale-[1.01] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60 disabled:scale-100"
                disabled={busy || !rawCode}
                onClick={handleCopyAndDownloadClick}
              >
                <span className="text-[18px] font-black leading-[1.3]">
                  {copied ? "Copied - opening download..." : "Copy code & download"}
                </span>
              </button>

              <button
                className="self-center cursor-pointer border-0 bg-transparent p-0 text-center text-[14px] font-black leading-[1.3] text-[#776f7b] underline-offset-4 hover:underline"
                onClick={openAppOrStore}
              >
                Download without copying
              </button>
            </div>
          ) : null}

          {isDesktop ? (
            <div className="mt-1 flex w-full flex-col items-center gap-4 rounded-[28px] bg-[#07001a] px-5 py-6 text-white">
              <div className="rounded-[18px] bg-white p-4">
                <QRCodeSVG
                  value={qrUrl}
                  size={172}
                  bgColor="#ffffff"
                  fgColor="#07001a"
                  level="M"
                />
              </div>
              <div className="flex flex-col items-center gap-1">
                <p className="m-0 text-[18px] font-black">
                  Scan this on your phone
                </p>
                <p className="m-0 max-w-[360px] text-[14px] font-bold leading-[1.45] text-white/70">
                  It opens this same referral page so you can copy the code and
                  download Evo from your phone.
                </p>
              </div>
            </div>
          ) : null}

          <p className="m-0 font-semibold text-[#9b94a3] text-[13px] leading-[1.5]">
            Downloading still works if you skip copying, but the code is what
            unlocks the free grades.
          </p>
        </div>
      </div>
    </div>
  );
}
